/**
 * Section sub-navigation + section shell (design system v1, Sep 2026).
 *
 *   - <TabRow/>      — the row of sub-nav tabs: a neutral segmented control
 *                      (never brand colours; the sidebar owns the plum "you are here").
 *   - <SectionTabs/> — the module header (the page's single h1 via PageHeader),
 *                      the TabRow, and an <Outlet/> for the active child route.
 *
 * Each tab is a real URL (deep-linkable, back-button works, active tab
 * auto-highlighted). Child pages render NO h1 of their own and share the page
 * frame (left-aligned, px-4 md:px-8) so the title, tabs and content line up.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { segGroup, segItemOff, segItemOn } from '../design/ui';

export interface SectionTab {
  to: string;
  label: string;
  /** Exact-match highlight (use for a section's index/landing tab). */
  end?: boolean;
  /** Extra path prefixes that should ALSO highlight this tab (e.g. a tab that
   *  fronts several nested routes living under different URLs). */
  match?: string[];
}

export function TabRow({ tabs, wrap = false }: {
  tabs: SectionTab[];
  wrap?: boolean;
  /** Kept for existing callers; both render the segmented control now. */
  variant?: 'segmented' | 'buttons';
}) {
  const { pathname } = useLocation();
  return (
    <nav aria-label="Section" className={wrap ? `${segGroup} flex-wrap` : segGroup}>
      {tabs.map((tab) => {
        const forced = tab.match?.some((m) => pathname === m || pathname.startsWith(`${m}/`)) ?? false;
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => (isActive || forced ? segItemOn : segItemOff)}
          >
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function SectionTabs({
  title,
  summary,
  tabs,
  wrap = false,
}: {
  title?: string;
  /** One italic sentence under the module title. */
  summary?: string;
  tabs: SectionTab[];
  wrap?: boolean;
}) {
  return (
    <>
      {/* Module header + tabs share the page frame; the <Outlet/> is left bare
          so each page keeps its own frame (same gutter, no double-wrap). */}
      <div className="w-full max-w-page px-4 md:px-8 pt-6 space-y-4">
        {title && <PageHeader title={title} summary={summary} />}
        {tabs.length > 1 && <TabRow tabs={tabs} wrap={wrap} />}
      </div>
      <Outlet />
    </>
  );
}
